/** @typedef {import('../../../shared/OfferService/OffersProps').default} Props */
/** @typedef {'agent' | 'group' | 'tenant' | 'local'} VendorMode */

import { translate } from '../../../Internationalization';
import AdminVendors from './AdminVendors';
import flags from '../../../../shared/Flags';
import FormSuccessMessages from '../../../../shared/Forms/Messages/FormSuccessMessages';
import images from '../../../../config/local/images';
import LinkButton from '../../../../shared/LinkButton';
import paths from '../../../../config/local/paths';
import React from 'react';
import useAuth from '../../../../Hooks/Shared/useAuth/useAuth';
import useIsGated from '../../../../Hooks/Shared/useIsGated/useIsGated';

const mode = /** @type {const} */ ({
	agent: 'agent',
	group: 'group',
	tenant: 'tenant',
	local: 'local',
});

/**
 * @param {boolean} isActive
 * @returns {string}
 */
function getVendorModeButtonClass(isActive) {
	return `button button-icon${isActive ? '' : ' button-outline'}`;
}

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
export default function ViewOffers(props) {
	const auth = useAuth();
	const isOwnerOrAdmin =
		auth.roles.includes('owner') || auth.roles.includes('admin');
	const [total, setTotal] = React.useState(0);
	const requestedMode = /** @type {VendorMode} */ (
		props.location?.state?.vendorMode ?? mode.agent
	);
	const activeMode = /** @type {VendorMode} */ (
		!isOwnerOrAdmin && requestedMode === mode.tenant
			? mode.agent
			: requestedMode
	);
	const gated = useIsGated(flags.vendor.add, { role: 'admin' });
	const isGated = isOwnerOrAdmin ? false : gated.isGated;

	const addVendorState =
		/** @type {import('../../../../shared/LinkButtonProps').default['to']} */ ({
			pathname: paths.app.admin.offers.add,
			state: {
				vendorMode: activeMode,
			},
		});

	return (
		<React.Fragment>
			<header className="mt-10 mb-6 md:flex justify-between items-center">
				<h1 className="hl-ms-6 mb-6 md:mb-0">
					{`${translate('admin.pages.offers.view.title')}`}{' '}
					{total ? `(${total})` : ''}
				</h1>

				<section className="button-group">
					<LinkButton
						className={getVendorModeButtonClass(
							activeMode === mode.group
						)}
						icon={images.icons.eye}
						to={
							/** @type {import('../../../../shared/LinkButtonProps').default['to']} */ ({
								pathname: paths.app.admin.offers.view,
								state: { vendorMode: mode.group },
							})
						}
					>
						{translate('admin.pages.offers.view.view_groups')}
					</LinkButton>

					{isOwnerOrAdmin ? (
						<LinkButton
							className={getVendorModeButtonClass(
								activeMode === mode.tenant
							)}
							icon={images.icons.eye}
							to={
								/** @type {import('../../../../shared/LinkButtonProps').default['to']} */ ({
									pathname: paths.app.admin.offers.view,
									state: { vendorMode: mode.tenant },
								})
							}
						>
							{translate('admin.pages.offers.view.view_tenant')}
						</LinkButton>
					) : null}

					{isOwnerOrAdmin ? (
						<LinkButton
							className={getVendorModeButtonClass(
								activeMode === mode.local
							)}
							icon={images.icons.eye}
							to={
								/** @type {import('../../../../shared/LinkButtonProps').default['to']} */ ({
									pathname: paths.app.admin.offers.view,
									state: { vendorMode: mode.local },
								})
							}
						>
							{translate('admin.pages.offers.view.local_pros')}
						</LinkButton>
					) : null}

					{!isGated ? (
						<LinkButton
							icon={images.icons.plus}
							to={addVendorState}
						>
							{translate('global.offers.add_title')}
						</LinkButton>
					) : null}
				</section>
			</header>

			{props.location?.state?.message ? (
				<FormSuccessMessages messages={props.location.state.message} />
			) : null}

			<AdminVendors mode={activeMode} onTotalChange={setTotal} />
		</React.Fragment>
	);
}
